import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  PageBodyTitle,
  Card,
  Table,
  ListPageFilterBar,
  PageLoader,
  StatusBadge,
} from "../../../design-system";
import { theme } from "../../../design-system/theme";
import styled from "styled-components";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-toastify";
import { CsvDown } from "../../../integrations/export";
import {
  soundboxInventoryList,
  allocateSoundboxRequest,
  deallocateSoundboxRequest,
  getAssignedUserDetailsRequest,
  getMerchantListingMob,
} from "../../../../utils/api";

import CsvDownApi from "../../../../components/ExportFiles/CsvDownApi";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { uploadSoundboxInventory } from "../../../../utils/api";
import Config from "../../../../utils/config";

//
// 🔹 FILTERS
//
const STATUS_OPTIONS = [
  { label: "ASSIGNED", value: "ASSIGNED" },
  { label: "UNASSIGNED", value: "UNASSIGNED" },
];

const FILTER_INITIAL = [
  { name: "imei", type: "text", value: "", placeholder: "Search by IMEI" },
  { name: "podTid", type: "text", value: "", placeholder: "Pod TID" },
  { name: "podSerialNumber", type: "text", value: "", placeholder: "Pod Serial Number" },
  { name: "fromDate", type: "date", value: "" },
  { name: "toDate", type: "date", value: "" },
  {
    name: "status",
    type: "select",
    value: "",
    placeholder: "Select Status",
    options: STATUS_OPTIONS,
  },
];
const CSV_HEADERS = [
  { label: "ID", key: "id" },
  { label: "IMEI", key: "imei" },
  { label: "POD TID", key: "podTid" },
  { label: "POD Serial Number", key: "podSerialNumber" },
  { label: "SIM No", key: "simNo" },
  { label: "POD Type", key: "podType" },
  { label: "Dispatch Location", key: "dispatchLocation" },
  { label: "Status", key: "status" },
  { label: "Created By", key: "createdBy" },
  { label: "Updated By", key: "updatedBy" },
  { label: "Created At", key: "createdAt" },
  { label: "Updated At", key: "updatedAt" },
];
//
// 🔹 STYLES
//
const PageWrapper = styled.div`
  max-width: 1440px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  margin-bottom: ${theme.spacing.lg};
  gap: ${theme.spacing.md};
`;

//
// 🔹 HELPERS
//
const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toISOString().split("T")[0];
};

const buildPayload = (controls, pageNo, pageSize) => {
  const get = (n) => controls.find((c) => c.name === n)?.value ?? "";

  return {
    imei: get("imei") || "",
    podTid: get("podTid") || "",
    podSerialNumber: get("podSerialNumber") || "",
    fromDate: get("fromDate") ? formatDate(get("fromDate")) : "",
    toDate: get("toDate") ? formatDate(get("toDate")) : "",
    status: get("status") || "",
    pagination: { pageNo, pageSize },
  };
};

//
// 🔹 ROW MENU (NEW)
//
const RowMenu = ({ row, onView, onAllocate }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>

      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItem
          disabled={row.status === "UNASSIGNED"}
          onClick={() => {
            handleClose();
            onView(row);
          }}
        >
          View
        </MenuItem>

        <MenuItem
          disabled={row.status === "ASSIGNED"}
          onClick={() => {
            handleClose();
            onAllocate(row);
          }}
        >
          Allocate
        </MenuItem>
      </Menu>
    </>
  );
};

//
// 🔥 MAIN COMPONENT
//
export default function SoundboxInventoryV2() {
  const [controls, setControls] = useState([...FILTER_INITIAL]);
  const [appliedControls, setAppliedControls] = useState([...FILTER_INITIAL]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [allocateItem, setAllocateItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  const [mobile, setMobile] = useState("");
  const [merchant, setMerchant] = useState(null);

  const [assignedUser, setAssignedUser] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [deallocating, setDeallocating] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [AllowName, setAllowName] = useState(true);
  const [csvData, setCsvData] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const fileInputRef = useRef(null);
  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  //
  // 🔹 FETCH DATA
  //
  const fetchData = useCallback((controlValues, pageNo, size) => {
    setLoading(true);

    const payload = buildPayload(controlValues, pageNo, size);

    soundboxInventoryList(payload)
      .then((res) => {
        const data = res?.data;

        if (data?.code !== "0000") {
          toast.error(data.message);
          setRows([]);
          return;
        }

        const meta = data.metaInfo;
        setRows(meta?.data ?? []);
        setTotalPages(meta?.totalPages ?? 1);
      })
      .catch(() => toast.error("API error"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData(appliedControls, page, pageSize);
  }, [appliedControls, page, pageSize, fetchData]);

  //
  // 🔹 FILTERS
  //
  const handleFilterChange = (name, value) => {
    setControls((prev) =>
      prev.map((c) => (c.name === name ? { ...c, value } : c))
    );
  };

  const onSearch = () => {
    setAppliedControls(controls.map((c) => ({ ...c })));
    setPage(1);
  };

  const onReset = () => {
    const reset = FILTER_INITIAL.map((f) => ({ ...f, value: "" }));
    setControls(reset);
    setAppliedControls(reset);
    setPage(1);
  };

  //
  // 🔹 ACTIONS
  //
  const handleAllocateClick = async () => {
  if (!merchant) return;

  setSubmitting(true);

  try {
    await allocateSoundboxRequest({
      imei: allocateItem?.imei,
      podSerialNumber: allocateItem?.podSerialNumber,
      userId: merchant?.id,
      isDeviceLink: true,
    });

    toast.success("Allocated successfully");
    setAllocateItem(null);
    setMobile("");
    setMerchant(null);
    fetchData(controlsRef.current, page, pageSize);
  } catch {
    toast.error("Allocation failed");
  } finally {
    setSubmitting(false);
  }
};
 
  const handleSearchClick = async () => {
  setLookupError("");
  setMerchant(null);

  if (!mobile || mobile.length !== 10) {
    setLookupError("Enter valid 10-digit mobile number");
    return;
  }

  setLookupLoading(true);

  try {
    const res = await getMerchantListingMob({
      mobileNumber: mobile,
      userType: "ROLE_BUSINESS_MERCHANT_USER",
      pagination: { pageNo: 1, pageSize: 10 },
    });

    const m = res?.data?.data?.content?.[0];

    if (!m) {
      setLookupError("Merchant not found");
    } else {
      setMerchant(m);
    }
  } catch {
    setLookupError("Something went wrong");
  } finally {
    setLookupLoading(false);
  }
};
const handleFileChange = (e) => {
  if (e.target.files?.length) {
    setSelectedFile(e.target.files[0]);
  }
};

const handleUpload = async () => {
  if (!selectedFile) return;

  setUploading(true);

  try {
    const res = await uploadSoundboxInventory(selectedFile);

    if (res?.data?.code === "0000") {
      toast.success("Upload successful");
      setUploadOpen(false);
      setSelectedFile(null);
      fetchData(controlsRef.current, page, pageSize);
    } else {
      toast.error(res?.data?.message || "Upload failed");
    }
  } catch (err) {
    toast.error(err?.message || "Upload failed");
  } finally {
    setUploading(false);
  }
};
const fetchAllDataForCSV = async () => {
  try {
    setCsvLoading(true);

    let pageNo = 1;
    let allData = [];
    let totalPages = 1;

    do {
      const payload = buildPayload(controlsRef.current, pageNo, 500);

      const res = await soundboxInventoryList(payload);
      const data = res?.data;

      if (data?.code !== "0000") break;

      const meta = data.metaInfo;

      const content = meta?.data || [];

      allData = [...allData, ...content];

      totalPages = meta?.totalPages || 1;
      pageNo++;

    } while (pageNo <= totalPages);

    setCsvData(allData);

  } catch (err) {
    toast.error("CSV fetch failed");
  } finally {
    setCsvLoading(false);
  }
};
  //
  // 🔹 VIEW + ASSIGNED USER
  //
  useEffect(() => {
    if (viewItem) {
      setUserLoading(true);

      const payload = {
        imei: viewItem?.imei,
        podTid: viewItem?.podTid,
        podSerialNumber: viewItem?.podSerialNumber,
        pagination: { pageNo: 1, pageSize: 10 },
        status: "ACTIVE",
      };

      getAssignedUserDetailsRequest(payload)
        .then((res) => {
          if (res?.data?.code === "0000") {
            setAssignedUser(res?.data?.metaInfo?.data?.[0]?.userProfile);
          } else {
            setAssignedUser(null);
          }
        })
        .catch(() => setAssignedUser(null))
        .finally(() => setUserLoading(false));
    }
  }, [viewItem]);
useEffect(() => {
  fetchAllDataForCSV();
}, [appliedControls]);
  //
  // 🔹 COLUMNS (UPDATED ACTIONS)
  //
  const columns = [
    { key: "id", label: "ID" },
    { key: "imei", label: "IMEI" },
    { key: "podTid", label: "POD TID" },
    { key: "podSerialNumber", label: "POD Serial Number" },
    { key: "simNo", label: "SIM No" },
    { key: "podType", label: "POD Type" },
    { key: "dispatchLocation", label: "Dispatch Location" },
    { key: "dataUpdated", label: "Data Updated" },
    { key: "simProvider", label: "SIM Provider" },
    {
      key: "status",
      label: "Status",
      render: (_, r) => (r?.status ? <StatusBadge status={r.status} /> : "-"),
    },
    { key: "createdBy", label: "Created By" },
    { key: "updatedBy", label: "Updated By" },
    {
      key: "createdAt",
      label: "Created At",
      render: (_, r) =>
        r.createdAt ? new Date(r.createdAt).toLocaleString() : "-",
    },
    {
      key: "updatedAt",
      label: "Updated At",
      render: (_, r) =>
        r.updatedAt ? new Date(r.updatedAt).toLocaleString() : "-",
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <RowMenu
          row={row}
          onView={setViewItem}
          onAllocate={setAllocateItem}
        />
      ),
    },
  ];

  if (loading && rows.length === 0) return <PageLoader />;

  return (
    <PageWrapper>
      <TopBar>
      <PageBodyTitle>Soundbox Inventory</PageBodyTitle>
    
      <div style={{ display: "flex", gap: 10, marginLeft: "auto", flexWrap: "wrap", justifyContent: "flex-end" }}>
     <CsvDown
      headers={CSV_HEADERS}
      exportRows={csvData} 
      title="Download CSV"
      reportName="soundbox-inventory.csv"
    />
   
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setUploadOpen(true)}
          sx={{
            fontSize: "12px",        // 👈 smaller text
            padding: "4px 10px",     // optional: reduce height
            minWidth: "auto"         // optional: compact width
          }}
        >
          Upload Inventory
        </Button>
      </div>
    </TopBar>

      <Card>
        <ListPageFilterBar
          filters={controls}
          onFilterChange={handleFilterChange}
          onSearch={onSearch}
          onReset={onReset}
        />

        <Table
          data={rows}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            currentPage: page,
            pageSize,
            total: totalPages * pageSize,
            onPageChange: setPage,
            onPageSizeChange: (s) => {
              setPageSize(s);
              setPage(1);
            },
          }}
        />
      </Card>
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" PaperProps={{sx: { width: "400px" }  // 🔥 controlled width
       }}>
         <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
           Upload Inventory
           <IconButton onClick={() => setUploadOpen(false)}>
             <CloseIcon />
           </IconButton>
         </DialogTitle>
       
         <DialogContent>
           <input
             ref={fileInputRef}
             type="file"
             accept=".csv"
             onChange={handleFileChange}
             style={{ marginTop: 10 }}
           />
       
           {selectedFile && (
             <Typography sx={{ mt: 1, fontSize: 13 }}>
               Selected: <strong>{selectedFile.name}</strong>
             </Typography>
           )}
       
           {/* SAMPLE DOWNLOAD */}
           <Button
             variant="text"
             onClick={() =>
               window.open(
                 "https://app-user-asset.s3.ap-south-1.amazonaws.com/sample-files/soundbox.csv",
                 "_blank"
               )
             }
             sx={{
               mt: 2,
               display: "flex",
               alignItems: "center",
               gap: 1,
               color: "red",
               textTransform: "none",
             }}
           >
             <DownloadIcon fontSize="small" />
             Download Sample
             <ChevronRightIcon fontSize="small" />
           </Button>
         </DialogContent>
       
         <DialogActions>
           <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
       
           <Button
             variant="contained"
             onClick={handleUpload}
             disabled={!selectedFile || uploading}
           >
             {uploading ? "Uploading..." : "Upload"}
           </Button>
         </DialogActions>
      </Dialog>
      {/* Allocate Modal */}
    <Dialog open={!!allocateItem} onClose={() => setAllocateItem(null)} maxWidth="sm" fullWidth>
  <DialogTitle
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontWeight: 600,
    }}
  >
    Allocate Device — {allocateItem?.imei || allocateItem?.podSerialNumber || allocateItem?.id}
    <IconButton onClick={() => setAllocateItem(null)} size="small">
      <CloseIcon />
    </IconButton>
  </DialogTitle>

  <DialogContent dividers>
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      
      <Typography variant="body2" color="text.secondary">
        Search by mobile number and click the Search button to lookup merchant.
      </Typography>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <TextField
          label="Mobile number"
          placeholder="Enter 10-digit mobile number"
          value={mobile}
          onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
          fullWidth
          inputProps={{ maxLength: 10 }}
          disabled={submitting}
        />

        <Button
          variant="contained"
          onClick={handleSearchClick}
          disabled={submitting || lookupLoading || !mobile}
        >
          {lookupLoading ? "Searching..." : "Search"}
        </Button>
      </div>

      <div style={{ minHeight: 28 }}>
        {lookupLoading && (
          <Typography variant="body2" color="text.secondary">
            Looking up merchant...
          </Typography>
        )}

        {!lookupLoading && lookupError && (
          <Typography variant="body2" color="error">
            {lookupError}
          </Typography>
        )}

        {!lookupLoading && merchant && AllowName && !lookupError && (
          <Typography variant="body2">
            Merchant: {merchant?.businessName || merchant?.name || "-"}
          </Typography>
        )}
      </div>
    </div>
  </DialogContent>

  <DialogActions sx={{ px: 3, pb: 2 }}>
    <Button
      variant="outlined"
      onClick={() => setAllocateItem(null)}
      disabled={submitting}
    >
      Cancel
    </Button>

    <Button
      variant="contained"
      onClick={handleAllocateClick}
      disabled={submitting || !merchant}
    >
      {submitting ? "Allocating..." : "Allocate"}
    </Button>
  </DialogActions>
</Dialog>

      {/* View Modal */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)}>
        <DialogTitle>Device Details— {viewItem?.imei || viewItem?.podSerialNumber || viewItem?.id}</DialogTitle>

        <DialogContent>
          {viewItem &&
            Object.entries(viewItem).map(([k, v]) => (
              <Typography key={k}>
                <b>{k}:</b> {String(v)}
              </Typography>
            ))}

          <Typography mt={2} fontWeight={600}>
            Assigned User
          </Typography>

          {userLoading ? (
            <Typography>Loading...</Typography>
          ) : assignedUser ? (
            <>
              <Typography> <strong>Name:</strong>  {assignedUser.fullName}</Typography>
              <Typography><strong>Mobile:</strong> {assignedUser.mobile}</Typography>
              <Typography><strong>Email:</strong>{assignedUser.email || "-"}</Typography>
               <Typography><strong>Status:</strong> {assignedUser.status || "-"}</Typography>
            </>
          ) : (
            <Typography>No assigned user</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={() => setViewItem(null)}>Close</Button>

          <Button
            variant="contained"
            disabled={!assignedUser || viewItem?.status === "UNASSIGNED" || deallocating}
            onClick={async () => {
              setDeallocating(true);
              try {
                await deallocateSoundboxRequest(assignedUser?.userUuid);
                toast.success("Deallocated successfully");
                setViewItem(null);
                fetchData(controlsRef.current, page, pageSize);
              } catch {
                toast.error("Deallocation failed");
              } finally {
                setDeallocating(false);
              }
            }}
          >
            {deallocating ? "Deallocating..." : "Deallocate"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
}